import { useTranslation } from "@sagot/i18n";
import { Icon, StatusBadge, Switch, Tooltip, cn, type IconName } from "@sagot/ui";
import type { ReactNode } from "react";
import type { ChannelOut } from "../shared/types";
import { statusKind } from "./channels-model";

export interface ChannelSwitch {
  checked: boolean;
  onChange: (enabled: boolean) => void;
  disabled?: boolean;
  /** Why the switch is disabled (tooltip), e.g. missing permission. */
  reason?: string;
}

interface ChannelCardProps {
  icon: IconName;
  title: string;
  /** Status line next to the badge (domain count, host, page name…). */
  meta?: ReactNode;
  status?: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  toggle?: ChannelSwitch;
  /** Replaces the switch slot (e.g. a "keys active" badge). */
  corner?: ReactNode;
  muted?: boolean;
  testId?: string;
}

function SwitchSlot({ toggle, title }: { toggle: ChannelSwitch; title: string }) {
  const { t } = useTranslation();
  const control = (
    <Switch
      checked={toggle.checked}
      disabled={toggle.disabled}
      onCheckedChange={toggle.onChange}
      aria-label={t("deployKnowledge.channels.enable", { channel: title })}
    />
  );
  if (!toggle.reason) return control;
  return (
    <Tooltip content={toggle.reason}>
      <span className="inline-flex">{control}</span>
    </Tooltip>
  );
}

/** Channel card shell (mockup `article.card`): icon + switch, title, status, description, footer. */
export function ChannelCard({ icon, title, meta, status, description, children, footer, toggle, corner, muted = false, testId }: ChannelCardProps) {
  return (
    <article className="card flex flex-col" data-testid={testId} aria-label={title}>
      <div className="card-body flex-1">
        <div className="flex items-start justify-between gap-3">
          <span className={cn("inline-flex size-10 items-center justify-center rounded-lg", muted ? "bg-sunken text-fg-muted" : "bg-primary-soft text-primary-ink")}>
            <Icon name={icon} className="size-5" />
          </span>
          {toggle ? <SwitchSlot toggle={toggle} title={title} /> : corner}
        </div>
        <h3 className="mt-3 text-sm font-bold">{title}</h3>
        {status || meta ? (
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            {status}
            {meta ? <span className="text-xs text-fg-subtle">{meta}</span> : null}
          </div>
        ) : null}
        {description ? <p className="mt-2 text-xs text-fg-muted">{description}</p> : null}
        {children}
      </div>
      {footer ? <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line px-5 py-3">{footer}</div> : null}
    </article>
  );
}

export function ChannelStatusBadge({ channel }: { channel: Pick<ChannelOut, "status"> }) {
  const { t } = useTranslation();
  return <StatusBadge status={statusKind(channel.status)} label={t(`deployKnowledge.channels.status.${channel.status}`)} />;
}

/** "784 conversations · 7d" footer metric. */
export function ConversationsMetric({ count, emails = false }: { count: number; emails?: boolean }) {
  const { t } = useTranslation();
  return (
    <span className="text-xs text-fg-subtle">
      <strong className="text-fg tabular-nums">{count}</strong> {t(emails ? "deployKnowledge.channels.emails7d" : "deployKnowledge.channels.conversations7d")}
    </span>
  );
}
